/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ConfirmPasswordValidator } from './confirm-password.validator';
import * as UserAuthActions from '../../../state/actions/user-authentification.actions';
import { DialogService } from 'src/app/core/services/dialog.service';
import { RegisteredUser } from 'src/app/core/models/entites/registeredUser';
import { UserState } from 'src/app/state/user-state';
import { Store, select } from '@ngrx/store';
import { ConfirmEmailValidator } from './confirm-email.validator';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { EmailValidator } from './emailValidator.validator';
import { Observable, Subscription, take } from 'rxjs';
import { selectAuthError } from 'src/app/state/reducer/user.reducer';
import { ICONS } from 'src/app/core/constants/icons';
import { ROUTES } from 'src/app/core/constants/routes';
import { environment } from 'src/environments/environment';
import { CloudflareTurnstileService } from 'src/app/core/services/cloudflare-turnstile.service';

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss']
})
export class RegistrationComponent implements OnInit, OnDestroy {
  protected registerError: string | undefined;
  LOGO = ICONS.NEW_LOGO;

  protected registrationForm = this.formBuilder.group(
    {
      name: ['', [Validators.required, Validators.pattern('^[a-zA-Ž]+$')]],
      surname: ['', [Validators.required, Validators.pattern('^[a-zA-Ž]+$')]],
      email: ['', [Validators.required, Validators.email, EmailValidator]],
      confirmEmail: ['', [Validators.required, Validators.email, EmailValidator]],
      phoneNumber: [
        '',
        [
          Validators.required,
          Validators.pattern('^\\+?[0-9]+$'),
          Validators.minLength(9),
          Validators.maxLength(13)
        ]
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(/^(?=.*[A-Z]|.*[^a-zA-Z0-9]).{8,}$/)
        ]
      ],
      confirmPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(/^(?=.*[A-Z]|.*[^a-zA-Z0-9]).{8,}$/)
        ]
      ],
      termsAndConditions: [false, Validators.requiredTrue],
      notMinor: [false, Validators.requiredTrue]
    },
    {
      validators: [
        ConfirmPasswordValidator('password', 'confirmPassword'),
        ConfirmEmailValidator('email', 'confirmEmail')
      ]
    }
  );

  protected PASSWORD = ICONS.NEW_PASSWORD;
  protected USERNAME = ICONS.NEW_USERNAME;
  protected PHONE = ICONS.NEW_PHONE;
  protected USER = ICONS.NEW_USER;
  protected LOGIN_ROUTE = ROUTES.LOGIN;
  protected ABOUT_APP = ROUTES.LOGIN_ABOUT_APP;

  private $registerError: Observable<string | undefined> = this.store.pipe(select(selectAuthError));
  private subscription: Subscription | undefined;
  private recaptchaToken: string = '';

  isNameInputFocused = false;
  isSurnameInputFocused = false;
  isEmailInputFocused = false;
  isConfirmEmailInputFocused = false;
  isPhoneNumberInputFocused = false;
  isPasswordInputFocused = false;
  isConfirmInputFocused = false;

  @ViewChild('turnstileContainer', { static: true })
  turnstileContainer!: ElementRef;

  siteKey = environment.siteKey;

  constructor(
    private formBuilder: FormBuilder,
    protected dialogService: DialogService,
    private store: Store<UserState>,
    protected spinnerService: SpinnerService,
    private captchaService: CloudflareTurnstileService
  ) {}

  ngOnInit() {
    this.spinnerService.hideSpinner();
    this.subscription = this.$registerError.subscribe((authError) => {
      this.registerError = authError;
    });
    this.store.dispatch(UserAuthActions.RESET_AUTH_ERROR_MESSAGE());
  }

  ngAfterViewInit() {
    this.captchaService.init(this.turnstileContainer.nativeElement, this.siteKey);
  }

  onSubmit() {
    if (!this.registrationForm.valid) {
      this.registrationForm.markAllAsTouched();
      return;
    }

    this.spinnerService.showSpinner();

    this.captchaService
      .execute()
      .pipe(take(1))
      .subscribe({
        next: (token) => {
          this.recaptchaToken = token;

          const registeredUser: RegisteredUser = {
            firstname: this.registrationForm.value.name as string,
            lastname: this.registrationForm.value.surname as string,
            email: this.registrationForm.value.email as string,
            reEmail: this.registrationForm.value.email as string,
            phoneNumber: this.registrationForm.value.phoneNumber as string,
            password: this.registrationForm.value.password as string,
            rePassword: this.registrationForm.value.confirmPassword as string,
            termsAndConditions: this.registrationForm.value.termsAndConditions as boolean,
            privacy: this.registrationForm.value.termsAndConditions as boolean,
            notMinor: this.registrationForm.value.notMinor as boolean
          };

          const dualRegisteredUser = {
            userRegisterDTO: registeredUser,
            captcha: this.recaptchaToken
          };

          this.store.dispatch(
            UserAuthActions.USER_REGISTRATION({ registeredUser: dualRegisteredUser })
          );

          this.captchaService.reset();
        },

        error: (err) => {
          this.captchaService.reset();
          this.spinnerService.hideSpinner();

          this.registerError = 'CAPTCHA verification failed. Please try again.';
        }
      });
  }

  get getRegisterForm() {
    return this.registrationForm.controls;
  }

  openPrivacyDialog() {
    this.dialogService.openPrivacy();
  }

  openTermsDialog() {
    this.dialogService.openTerms();
  }

  convertToLower(element: any) {
    element.value = element.value.toLowerCase();
  }

  inputChanged(event: any) {
    if (this.registerError !== '') this.store.dispatch(UserAuthActions.RESET_AUTH_ERROR_MESSAGE());
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.captchaService.destroy();
  }
}
